import React from 'react';
import { Alert, AlertTitle, Button, Box, Collapse, IconButton } from '@mui/material';
import { NotificationsActive, Close } from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';

const NotificationPermissionBanner: React.FC = () => {
  const { fcmPermission, requestNotificationPermission } = useNotifications();
  const [dismissed, setDismissed] = React.useState(false);

  // Don't show if permission is granted or user dismissed
  if (fcmPermission === 'granted' || dismissed) {
    return null;
  }

  // Don't show if permission is denied (user explicitly denied)
  if (fcmPermission === 'denied') {
    return null;
  }

  return (
    <Collapse in={!dismissed}>
      <Box sx={{ pb: 2 }}>
        <Alert
          severity="info"
          icon={<NotificationsActive />}
          sx={{
            // MUI puts `action` in a fixed-width column beside the message. On
            // a phone that squeezed the text into roughly six words per line
            // and made the banner taller than the content below it. Below `sm`
            // the action drops to its own full-width row instead.
            alignItems: { xs: 'flex-start', sm: 'center' },
            '& .MuiAlert-message': { width: '100%' },
            '& .MuiAlert-action': {
              width: { xs: '100%', sm: 'auto' },
              pl: { xs: 0, sm: 2 },
              pt: { xs: 1, sm: 0 },
              mr: { xs: 0, sm: -0.5 },
            },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
          action={
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                width: '100%',
                justifyContent: { xs: 'space-between', sm: 'flex-end' },
              }}
            >
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={requestNotificationPermission}
              >
                Enable Notifications
              </Button>
              <IconButton
                aria-label="Dismiss notification prompt"
                color="inherit"
                size="small"
                onClick={() => setDismissed(true)}
              >
                <Close fontSize="inherit" />
              </IconButton>
            </Box>
          }
        >
          <AlertTitle sx={{ mb: 0.25 }}>Enable Push Notifications</AlertTitle>
          Get real-time alerts for new tasks, ninja approvals, and important updates even when the
          admin panel is closed.
        </Alert>
      </Box>
    </Collapse>
  );
};

export default NotificationPermissionBanner;
